import './App.scss'
import { Provider as StyletronProvider } from 'styletron-react'
import { BaseProvider, Theme } from 'baseui'
import { useEffect, useState } from 'react'

import { getTheme, initTheme, theme } from '../../theming'
import { Route, Routes, useSearchParams } from 'react-router-dom'
import LanguageSelector, { LANGUAGES } from '../../components/LanguageSelector'
import { styletron } from '../../styletron'
import { ToasterContainer } from 'baseui/toast'
import { StepperContextProvider } from '../../contexts/StepperContext'
import { useDocumentMeta } from '../../hooks/useDocumentMeta'

import { Stepper } from '../../components/Stepper'
import Welcome from '../../components/Welcome'
import Otp from '../Otp/Otp'
import Identification from '../Identification/Identification'
import Sign from '../Sign/Sign'
import FinishedSign from '../FinishedSign/FinishedSign'
import i18n from '../../i18n'
import Validate from '../Validate/Validate'
import EditDocument from '../EditDocument/EditDocument'
import QrSign from '../QrSign/QrSign'
import UploadDocuments from '../UploadDocuments/UploadDocuments'
import { ParagraphSmall } from 'baseui/typography'
import { useTranslation } from 'react-i18next'

export enum STATES {
  BAD_REQUEST = '400',
  ERROR403 = '403',
  NON_PENDANT_OPERATION = '2400'
}

export enum AppRoutes {
  ROOT = "/",
  OTP = "/otp",
  IDENTIFICATION = "/identification",
  EDIT_DOCUMENTS = "/edit-documents",
  UPLOAD_DOCUMENTS = "/upload-documents",
  SIGN = "/sign",
  FINISHED_SIGN = "/finishedsign",
  VALIDATE = "/validate",
  QR_SIGN = "/qrSign"
}

const App = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const params: any = {}
  searchParams.forEach((value: string, key: string) => {
    params[key] = value
  })

  const [baseTheme, setData] = useState<Theme>(getTheme())
  const [componentLoaded, setComponentLoaded] = useState(false)

  // Set document title and <html lang> dynamically based on current route and language.
  useDocumentMeta()

  useEffect(() => {
    setLanguageByLangUrlParam()
    setTheme()
    setComponentLoaded(true)
  }, [])

  useEffect(() => {
    if (componentLoaded) {
      // Hacer cualquier llamada asíncrona a la API aqui
    }
  }, [componentLoaded])
  async function setLanguageByLangUrlParam() {
    const languageFinded = LANGUAGES.find((lang) => lang.region === params.lang)
    if (languageFinded) {
      i18n.changeLanguage(languageFinded.region)
    }
  }

  async function setTheme() {
    await initTheme()
    const baseTheme = getTheme()
    setData(baseTheme)
  }

  return componentLoaded ? (
    <div className="App">
      <StyletronProvider value={styletron}>
        <ToasterContainer autoHideDuration={7000}>
          <StepperContextProvider>
            <BaseProvider theme={baseTheme} key="base-provider">
              <header>
                <div className="logo-container">
                  <img
                    height={theme.logo.height}
                    width={theme.logo.width}
                    src={theme.logo.src}
                    alt="Rubricae logo"
                  />
                </div>
                <div className="lang-container">
                  {theme.elements.languageSelector && <LanguageSelector />}
                </div>
              </header>

              <main>
                <Stepper />
                <Routes>
                  <Route path={AppRoutes.ROOT} element={<Welcome />} />
                  <Route path={AppRoutes.OTP} element={<Otp />} />
                  <Route
                    path={AppRoutes.IDENTIFICATION}
                    element={<Identification />}
                  />
                  <Route
                    path={AppRoutes.EDIT_DOCUMENTS}
                    element={<EditDocument />}
                  />
                  <Route
                    path={AppRoutes.UPLOAD_DOCUMENTS}
                    element={<UploadDocuments />}
                  />
                  <Route path={AppRoutes.SIGN} element={<Sign />} />
                  <Route
                    path={AppRoutes.FINISHED_SIGN}
                    element={<FinishedSign />}
                  />
                  <Route path={AppRoutes.VALIDATE} element={<Validate />} />
                  <Route path={AppRoutes.QR_SIGN} element={<QrSign />} />
                </Routes>
              </main>

              
              <div className="accessibility-container">
                <a
                  className="accessibility-link"
                  href="https://rubricae.com/accesibilidad/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ParagraphSmall>
                    {t('ACCESSIBILITY')}
                  </ParagraphSmall>
                </a>
              </div>
            </BaseProvider>
          </StepperContextProvider>
        </ToasterContainer>
      </StyletronProvider>
    </div>
  ) : (
    <div className="App">Loading...</div>
  )
}

export default App
